import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Page from "../components/layout/Page";
import { blogPosts as blogPostsService } from "../services/api";

// Blog post data interface
interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  author: {
    name: string;
    email: string;
  };
  category: {
    name: string;
    slug: string;
    color?: string;
  };
  tags: {
    id: number;
    name: string;
    slug: string;
    color?: string;
  }[];
  featured_image?: string;
  featured_image_alt?: string;
  is_featured: boolean;
  views_count: number;
  reading_time_minutes?: number;
  published_at: string;
}

// Placeholder colors for categories and tags
const getCategoryColor = (categorySlug: string): string => {
  const colors: { [key: string]: string } = {
    technology: "#007bff",
    tutorial: "#28a745",
    commercial: "#ffc107",
    sustainability: "#17a2b8",
    security: "#dc3545",
    entertainment: "#fd7e14",
  };
  return colors[categorySlug] || "#6c757d";
};

const getTagColor = (tagSlug: string): string => {
  const colors: { [key: string]: string } = {
    "smart-home": "#007bff",
    automation: "#28a745",
    iot: "#17a2b8",
    loxone: "#fd7e14",
    guide: "#6c757d",
  };
  return colors[tagSlug] || "#6c757d";
};

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBlogPosts();
  }, []);

  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      const response = (await blogPostsService.list({
        limit: 100,
        status: "published",
      })) as any;

      console.log("Blog posts response:", response);

      if (response && response.posts) {
        const enrichedPosts = response.posts.map((post: any) => ({
          ...post,
          // Transform backend response to match interface
          author: {
            name: post.author_name || post.author?.name || "Unknown",
            email: post.author_email || post.author?.email || "",
          },
          category: {
            name: post.category_name || post.category?.name || "Uncategorized",
            slug: post.category_slug || post.category?.slug || "",
            color:
              post.category?.color ||
              post.category_color ||
              getCategoryColor(post.category_slug || post.category?.slug || ""),
          },
          tags: Array.isArray(post.tags)
            ? post.tags.map((tag: any) => ({
                id: tag.id,
                name: tag.name,
                slug: tag.slug,
                color: tag.color || getTagColor(tag.slug),
              }))
            : [],
          reading_time_minutes: post.reading_time_minutes || 5,
        }));
        setPosts(enrichedPosts);
      }
    } catch (error) {
      console.error("Error loading blog posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ro-RO", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Page>
        <div className="blog-index">
          <div className="loading-spinner"></div>
          <p>Loading blog posts...</p>
        </div>
      </Page>
    );
  }

  const featuredPosts = posts.filter((post) => post.is_featured);
  const regularPosts = posts.filter((post) => !post.is_featured);

  return (
    <Page>
      <div
        className="an-section spacing-small bg-light-primary mt0"
        id="services"
      >
        <div className="an-title">
          <h2>Blog</h2>
          <p>
            Descoperă cele mai recente articole, ghiduri și noutăți despre
            automatizări pentru case inteligente, tehnologie și inovații în
            domeniul locuințelor conectate.
          </p>
        </div>
      </div>

      <div className="blog-index">
        {/* Featured Posts */}
        {featuredPosts.length > 0 && (
          <section className="featured-section">
            <h2>Articole recomandate</h2>
            <div className="featured-posts">
              {featuredPosts.map((post) => (
                <article key={post.id} className="featured-post">
                  <Link to={`/blog/${post.slug}`} className="post-link">
                    {post.featured_image && (
                      <div className="post-image">
                        <img
                          src={post.featured_image}
                          alt={post.featured_image_alt}
                        />
                      </div>
                    )}
                    <div className="post-content">
                      <div className="post-meta">
                        <span
                          className="category-badge"
                          style={{ backgroundColor: post.category.color }}
                        >
                          {post.category.name}
                        </span>
                        <span className="reading-time">
                          <i className="fa-solid fa-clock"></i>
                          {post.reading_time_minutes} min read
                        </span>
                      </div>
                      <h3>{post.title}</h3>
                      <p>{post.excerpt}</p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Regular Posts */}
        <section className="posts-section">
          <h2>Ultimele articole</h2>
          <div className="posts-grid">
            {regularPosts.map((post) => (
              <article key={post.id} className="post-card">
                <Link to={`/blog/${post.slug}`} className="post-link">
                  {post.featured_image && (
                    <div className="post-image">
                      <img
                        src={post.featured_image}
                        alt={post.featured_image_alt}
                      />
                    </div>
                  )}
                  <div className="post-content">
                    <div className="post-meta">
                      <span
                        className="category-badge"
                        style={{ backgroundColor: post.category.color }}
                      >
                        {post.category.name}
                      </span>
                      <span className="reading-time">
                        <i className="fa-solid fa-clock"></i>
                        {post.reading_time_minutes} min read
                      </span>
                    </div>
                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </section>
      </div>
    </Page>
  );
}
